/**
 * Client for a brand's arrangement of a site page: the order of its sections, which of them are
 * hidden, and the sections the brand added showing its own lists.
 *
 * The model the backend enforces, and the reason this is not a list of ids like the other kinds:
 *
 * A platform section is named by the data source it renders (`ds_id`), NOT by its index. The
 * platform edits its own page, and an index means a different section the moment one is inserted
 * above it. Sections that render no list fall back to their type plus their place among sections of
 * that type.
 *
 * An arrangement LAYERS on the platform's page rather than freezing it. A platform section the
 * arrangement does not mention is one the platform has added since the owner last arranged the
 * page; it keeps its place at the end rather than disappearing. Hiding is not removal either: the
 * section stays on the page as inactive, so the platform's own props survive a hide and return
 * whole when it is shown again.
 *
 * The backend contract is `/creatives/casino-instance/{id}/page-sections` in
 * `spark-creatives/pkg/swagger/swagger.yml`.
 */
import axios from 'axios';

import { creativesRequestConfig, creativesServiceBase, creativesServiceConfigured } from './creative-api';
import { tenantSourceResult, type TenantSourceResult } from './creatives-tenant-source';
import { sharedReads } from './shared-read';
import { dropSitePages } from './site-pages-api';

/** The grid layouts the site ships. A carousel's layout is free-form and arranged through its own
 *  props (per-view counts, breakpoints, aspect ratio), so it has no name here. */
export const GRID_LAYOUTS = ['cdr-01', 'cdr-02', 'cal-01', 'cal-02', 'pldt-01'] as const;
export type GridLayout = (typeof GRID_LAYOUTS)[number];

/** How a brand's own section renders its list. */
export type OwnerSectionKind = 'games_carousel' | 'games_grid';

/** One of the PLATFORM's sections, named the way the backend names it. */
export interface PageSectionRef {
  ds_id: string;
  type: string;
  nth: number;
}

/** A section the brand added, showing one of its own lists. `title` is per language code, with `en`
 *  as the site's fallback. An empty `link` means the platform's own "See All" target, `/games/<list>`. */
export interface PageOwnerSection {
  list_id: string;
  title: Record<string, string>;
  kind: OwnerSectionKind;
  layout: string;
  link: string;
}

/** One row of an arrangement: exactly one of `platform` or `owner` is set. */
export interface PageArrangedSection {
  platform?: PageSectionRef;
  owner?: PageOwnerSection;
  hidden: boolean;
}

/** A brand's page, top to bottom. */
export interface PageArrangement {
  order: PageArrangedSection[];
}

/** Full state of one page: the platform's own sections in their order, the brand's working DRAFT
 *  and PUBLISHED arrangements, and whether a revert is currently possible. */
export interface PageSectionsState {
  source_id: string;
  platform: PageSectionRef[];
  draft: PageArrangement;
  published: PageArrangement;
  can_revert: boolean;
}

const pageSectionsPath = (casinoInstanceId: string): string =>
  `${creativesServiceBase()}/creatives/casino-instance/${encodeURIComponent(casinoInstanceId)}/page-sections`;

const str = (value: unknown): string => (typeof value === 'string' ? value : '');

const isOwnerSectionKind = (value: unknown): value is OwnerSectionKind =>
  value === 'games_carousel' || value === 'games_grid';

/** Whether a layout name is one the site ships as a grid. A carousel's free-form layout never is,
 *  so this narrows a stored string to a grid layout the picker can show. */
export const isGridLayout = (value: string): value is GridLayout =>
  (GRID_LAYOUTS as readonly string[]).includes(value);

const parseSectionRef = (body: Record<string, unknown>): PageSectionRef => ({
  ds_id: str(body.ds_id),
  type: str(body.type),
  nth: typeof body.nth === 'number' ? body.nth : 0,
});

/** Per-language titles, dropping any entry whose value is not a string. */
const parseTitle = (value: unknown): Record<string, string> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return {};
  const out: Record<string, string> = {};
  for (const [lang, text] of Object.entries(value as Record<string, unknown>)) {
    if (typeof text === 'string') out[lang] = text;
  }
  return out;
};

const parseOwnerSection = (body: Record<string, unknown>): PageOwnerSection => ({
  list_id: str(body.list_id),
  title: parseTitle(body.title),
  kind: isOwnerSectionKind(body.kind) ? body.kind : 'games_carousel',
  layout: str(body.layout),
  link: str(body.link),
});

/** Parse one row. A row naming neither a platform section nor an owner list is dropped by
 *  {@link parseArrangement}: it would render as nothing and could not be moved back. */
const parseArrangedSection = (body: Record<string, unknown>): PageArrangedSection => {
  const platform = typeof body.platform === 'object' && body.platform !== null ? body.platform : null;
  const owner = typeof body.owner === 'object' && body.owner !== null ? body.owner : null;
  return {
    ...(platform ? { platform: parseSectionRef(platform as Record<string, unknown>) } : {}),
    ...(owner ? { owner: parseOwnerSection(owner as Record<string, unknown>) } : {}),
    hidden: body.hidden === true,
  };
};

/** Parse an arrangement, keeping only rows that name a section. */
export const parseArrangement = (value: unknown): PageArrangement => {
  const body = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
  const order = (Array.isArray(body.order) ? body.order : [])
    .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
    .map(parseArrangedSection)
    .filter((row) => row.platform !== undefined || row.owner !== undefined);
  return { order };
};

/** Parse a `GET .../page-sections` body. A missing field degrades to an empty-but-typed state
 *  rather than throwing, so a partial or stale response still renders the page. */
export const parsePageSectionsState = (body: Record<string, unknown>): PageSectionsState => ({
  source_id: str(body.source_id),
  platform: (Array.isArray(body.platform) ? body.platform : [])
    .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
    .map(parseSectionRef),
  draft: parseArrangement(body.draft),
  published: parseArrangement(body.published),
  can_revert: body.can_revert === true,
});

/** The domain a page's read or write names the brand's site by: needed for a page under one of its list pages that the
 *  platform has not made yet (`SitePage.pageSourceId`), which the service reads as an empty page, keeps a draft of and
 *  makes on its first publish only for the site the domain names. '' names none. */
const withDomain = <T extends object>(body: T, domain: string): T | (T & { domain: string }) =>
  domain ? { ...body, domain } : body;

/** Read one page's full arrangement state. `domain` names the brand's site (see `withDomain`). */
export const getPageSections = async (
  casinoInstanceId: string,
  sourceId: string,
  domain = '',
  signal?: AbortSignal,
): Promise<TenantSourceResult<PageSectionsState>> => {
  if (!creativesServiceConfigured()) return { ok: false, reason: 'unavailable' };
  try {
    const site = domain ? `&domain=${encodeURIComponent(domain)}` : '';
    const response = await axios.get(
      `${pageSectionsPath(casinoInstanceId)}?source_id=${encodeURIComponent(sourceId)}${site}`,
      creativesRequestConfig(signal),
    );
    return tenantSourceResult(response.status, response.data, parsePageSectionsState);
  } catch {
    return { ok: false, reason: 'failed' };
  }
};

// The page reads every reader shares, per brand and page (see `shared-read`).
const pageReads = sharedReads<PageSectionsState>();
const pageKey = (casinoInstanceId: string, sourceId: string): string => `${casinoInstanceId}|${sourceId}`;

/** `getPageSections`, shared by every reader of the brand's page for `SHARED_READ_MS`: the fresh answer, else the read
 *  in flight, else one new read. It carries no reader's signal, so one reader leaving never cancels it for another; a
 *  reader checks its own abort once it has the answer. Every write of the page below drops it before it is sent and
 *  again once it settles: a read answered meanwhile may hold the page from before the write. */
export const getPageSectionsShared = (
  casinoInstanceId: string,
  sourceId: string,
  domain = '',
): Promise<TenantSourceResult<PageSectionsState>> =>
  pageReads.get(pageKey(casinoInstanceId, sourceId), () => getPageSections(casinoInstanceId, sourceId, domain));

/** Forget the shared read of the brand's page `sourceId`, or of every page of the brand without one: the next reader
 *  asks the service again. A read in flight now still answers its reader, but is not kept. */
export const dropPageSections = (casinoInstanceId: string, sourceId?: string): void => {
  const brand = pageKey(casinoInstanceId, '');
  const page = sourceId === undefined ? null : pageKey(casinoInstanceId, sourceId);
  pageReads.drop((key) => (page === null ? key.startsWith(brand) : key === page));
};

/** What a write of the live page changes: the page's shared read, and the brand's site pages, whose lists follow the
 *  live page. */
const dropLivePage = (casinoInstanceId: string, sourceId: string): void => {
  dropPageSections(casinoInstanceId, sourceId);
  dropSitePages(casinoInstanceId);
};

/** Autosave the working DRAFT arrangement (published is untouched). `domain` names the brand's site (see `withDomain`).
 *
 *  It acknowledges rather than returning the state: the caller already holds the arrangement it
 *  just sent, and rebuilding the state would re-read the platform's page from wl-data on every
 *  autosave. Publish and revert DO return it, because both change what the platform row means. */
export const savePageArrangementDraft = async (
  casinoInstanceId: string,
  sourceId: string,
  arrangement: PageArrangement,
  domain = '',
  signal?: AbortSignal,
): Promise<TenantSourceResult<true>> => {
  if (!creativesServiceConfigured()) return { ok: false, reason: 'unavailable' };
  dropPageSections(casinoInstanceId, sourceId);
  try {
    const response = await axios.put(
      `${pageSectionsPath(casinoInstanceId)}/draft`,
      withDomain({ source_id: sourceId, arrangement }, domain),
      creativesRequestConfig(signal),
    );
    return tenantSourceResult(response.status, response.data, () => true as const);
  } catch {
    return { ok: false, reason: 'failed' };
  } finally {
    dropPageSections(casinoInstanceId, sourceId);
  }
};

/** Put the brand's arrangement on the live site. `domain` names the brand's site (see `withDomain`). */
export const publishPageArrangement = async (
  casinoInstanceId: string,
  sourceId: string,
  arrangement: PageArrangement,
  domain = '',
  signal?: AbortSignal,
): Promise<TenantSourceResult<PageSectionsState>> => {
  if (!creativesServiceConfigured()) return { ok: false, reason: 'unavailable' };
  dropLivePage(casinoInstanceId, sourceId);
  try {
    const response = await axios.post(
      `${pageSectionsPath(casinoInstanceId)}/publish`,
      withDomain({ source_id: sourceId, arrangement }, domain),
      creativesRequestConfig(signal),
    );
    return tenantSourceResult(response.status, response.data, parsePageSectionsState);
  } catch {
    return { ok: false, reason: 'failed' };
  } finally {
    dropLivePage(casinoInstanceId, sourceId);
  }
};

/** Put the brand back on the arrangement before this one; with none, the platform's own page
 *  returns whole. */
export const revertPageArrangement = async (
  casinoInstanceId: string,
  sourceId: string,
  signal?: AbortSignal,
): Promise<TenantSourceResult<PageSectionsState>> => {
  if (!creativesServiceConfigured()) return { ok: false, reason: 'unavailable' };
  dropLivePage(casinoInstanceId, sourceId);
  try {
    const response = await axios.post(
      `${pageSectionsPath(casinoInstanceId)}/revert`,
      { source_id: sourceId },
      creativesRequestConfig(signal),
    );
    return tenantSourceResult(response.status, response.data, parsePageSectionsState);
  } catch {
    return { ok: false, reason: 'failed' };
  } finally {
    dropLivePage(casinoInstanceId, sourceId);
  }
};
